import { AlertCircle, X } from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { cn } from "@/lib/utils";

interface CrmTaskDialogErrorBannerProps {
  message: string | null | undefined;
}

interface CrmTaskDialogShellProps {
  title: string;
  subtitle?: string | null;
  closeLabel: string;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  maxWidthClassName?: string;
  children: ReactNode;
}

interface CrmTaskDialogFooterProps {
  onCancel: () => void;
  submitLabel: string;
  submittingLabel: string;
  isSubmitting: boolean;
  submitDisabled?: boolean;
  cancelLabel?: string;
  className?: string;
}

interface CrmTaskSkipReasonPanelProps {
  reason: string;
  onReasonChange: (value: string) => void;
}

export function CrmTaskDialogShell({
  title,
  subtitle,
  closeLabel,
  onClose,
  onSubmit,
  maxWidthClassName = "max-w-lg",
  children,
}: CrmTaskDialogShellProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <form
        onSubmit={onSubmit}
        className={cn(
          "max-h-[90vh] w-full overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-xl",
          maxWidthClassName,
        )}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
            aria-label={closeLabel}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </form>
    </div>
  );
}

export function CrmTaskDialogFooter({
  onCancel,
  submitLabel,
  submittingLabel,
  isSubmitting,
  submitDisabled,
  cancelLabel = "Cancel",
  className,
}: CrmTaskDialogFooterProps) {
  return (
    <div className={cn("mt-5 flex justify-end gap-2", className)}>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
      >
        {cancelLabel}
      </button>
      <button
        type="submit"
        disabled={Boolean(submitDisabled) || isSubmitting}
        className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] transition-colors hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
      >
        {isSubmitting ? submittingLabel : submitLabel}
      </button>
    </div>
  );
}

export function CrmTaskDialogErrorBanner({ message }: CrmTaskDialogErrorBannerProps) {
  if (!message) return null;

  return (
    <div className="mb-3 rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
      <AlertCircle className="mr-1 inline h-4 w-4" />
      {message}
    </div>
  );
}

export function CrmTaskSkipReasonPanel({ reason, onReasonChange }: CrmTaskSkipReasonPanelProps) {
  return (
    <label className="block text-sm">
      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Why is this no longer needed?</span>
      <textarea
        value={reason}
        onChange={(event) => onReasonChange(event.target.value)}
        rows={3}
        placeholder="Capture the reason so this cancellation is auditable and the next automation can learn from it."
        className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)]"
        autoFocus
      />
    </label>
  );
}
